import type { JournalEntry, Notification, Project } from '../../lib/api/types'

interface Props {
  journal: JournalEntry | null
  notification: Notification
  project: Project | null
  sortOrder: number
}

/** The A4 "Underrättelse" sheet, extracted from NotificationPrint so it can be
 *  rendered both standalone and stacked in the combined print (#12356). */
export default function NotificationPaper({ journal, notification, project, sortOrder }: Props) {
  const nr = `ÄTA-${String(sortOrder).padStart(3, '0')}`
  const proj: Partial<Project> = project ?? {}

  return (
    <div className="paper">
      {/* Letterhead */}
      <div className="paper-letterhead">
        <div>
          <div className="paper-sender-name">TIBRAM BYGG AB</div>
          <div className="paper-sender-addr">
            {proj.adress && <>{proj.adress}<br /></>}
            {proj.postadress && <>{proj.postadress}<br /></>}
            {proj.telPlatskontor && <>Tel: {proj.telPlatskontor}</>}
          </div>
        </div>
        <div>
          <div className="paper-doc-type">Underrättelse om ÄTA-arbete</div>
          <div className="paper-doc-ref">Ärende {nr}</div>
          <div style={{ fontFamily: 'monospace', fontSize: '9pt', textAlign: 'right', color: '#666', marginTop: '3pt' }}>
            Projekt {proj.projektnr ?? '–'}
          </div>
        </div>
      </div>

      {/* Address window */}
      <div className="paper-address-window">
        <div>
          <div className="paper-address-label">Till</div>
          <div className="paper-address-name">{proj.bestRef ?? proj.bestellare ?? '–'}</div>
          <div className="paper-address-body">
            {proj.bestellare && <>{proj.bestellare}<br /></>}
          </div>
        </div>
        <div>
          <div className="paper-meta-key">Datum</div>
          <div className="paper-meta-val">{notification.datum ?? new Date().toISOString().slice(0,10)}</div>
          <div style={{ marginTop: '8pt' }}>
            <div className="paper-meta-key">Vår referens</div>
            <div className="paper-meta-val">{proj.platschef ?? '–'}</div>
          </div>
        </div>
      </div>

      <hr className="paper-divider" />

      {/* Subject */}
      <div>
        <div className="paper-subject-label">Ärende</div>
        <div className="paper-subject-title">
          Underrättelse om ÄTA-arbete — {journal?.arbeteBeskrivning ?? nr}
        </div>
        <div style={{ fontSize: '10pt', color: '#555', marginTop: '3pt' }}>
          {proj.projektnr && proj.projektNamn
            ? `${proj.projektnr} · ${proj.projektNamn}${proj.ort ? ', ' + proj.ort : ''}`
            : proj.projektNamn ?? ''}
        </div>
      </div>

      <hr className="paper-divider" />

      {/* Body */}
      <div className="paper-body">
        <p>
          Härmed underrättas Ni i enlighet med ABT 06 kap. 2 § 6 om att
          nedanstående tillkommande arbete uppkommit och att entreprenören
          avser att genomföra detta arbete.
        </p>

        <div className="paper-section-label">Arbete</div>
        <p>{journal?.arbeteBeskrivning ?? '–'}</p>

        {journal?.orsak && (
          <>
            <div className="paper-section-label">Orsak</div>
            <p>{journal.orsak}</p>
          </>
        )}

        {(notification.omstandighet || journal?.arbeteDetaljer) && (
          <>
            <div className="paper-section-label">Beskrivning</div>
            <p style={{ whiteSpace: 'pre-wrap' }}>
              {notification.omstandighet || journal?.arbeteDetaljer}
            </p>
          </>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16pt', marginTop: '10pt' }}>
          <div>
            <div className="paper-section-label">Påverkan tid</div>
            <p>{journal?.paverkanTid || 'Bedöms'}</p>
          </div>
          <div>
            <div className="paper-section-label">Påverkan ekonomi</div>
            <p>{journal?.paverkanEkonomi || 'Bedöms'}</p>
          </div>
        </div>

        <p style={{ marginTop: '14pt' }}>
          Specificerad kostnadsberäkning bifogas/överlämnas snarast möjligt
          efter det att ÄTA-arbetets omfattning kan fastställas.
        </p>

        <p>
          Vi inväntar beställarens skriftliga besked angående detta ÄTA-arbete.
        </p>
      </div>

      {/* Signature */}
      <div className="paper-signature">
        <div>
          <div className="paper-sig-line" />
          <div className="paper-sig-label">
            {proj.platschef ?? 'Platschef'}<br />
            Platschef, Tibram Bygg AB
          </div>
        </div>
        <div>
          <div className="paper-sig-line" />
          <div className="paper-sig-label">
            Datum och underskrift, beställare
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="paper-footer">
        {nr} · {proj.projektnr ?? '–'} &nbsp;—&nbsp; Sida 1 (1)
      </div>
    </div>
  )
}
